import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Truck, Award, Star, ChevronRight, Zap, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import TrustedClients from '../components/TrustedClients';
import SocialFeed from '../components/SocialFeed';

// Static data for homepage showcase
const featuredCategories = [
  {
    id: 'business-cards',
    name: 'Business Cards',
    description: 'Premium business cards with multiple finish options',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80',
  },
  {
    id: 'flyers-brochures',
    name: 'Flyers & Brochures',
    description: 'Eye-catching marketing materials for any campaign',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
  },
  {
    id: 'marketing-materials',
    name: 'Marketing Materials',
    description: 'Complete marketing solutions for your business',
    image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80',
  }
];

const testimonials = [
  {
    id: 1,
    name: 'Mike S.',
    company: 'Verified Customer',
    rating: 5,
    text: 'They did amazing work and a very fast turnaround time. They have saved me many times, I will keep coming back to them.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80'
  },
  {
    id: 2,
    name: "Marty's Customer",
    company: 'Verified Customer',
    rating: 5,
    text: 'Went in looking for a couple of awards, Marty was kind and super helpful! Got everything exactly the way I wanted in a good timeline.',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80'
  },
  {
    id: 3,
    name: 'Verified Customer',
    company: 'Santa Ana, CA',
    rating: 5,
    text: 'They were amazing! Very friendly and professional, got back to me right away. Their work is excellent and timely. I highly recommend them.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80'
  },
  {
    id: 4,
    name: "Manny's Customer",
    company: 'Verified Customer',
    rating: 5,
    text: "Very nice and patient, Manny helped me with my two little projects. He delivered right on time (as promised) and to be honest I don't think it was expensive at all.",
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80'
  },
  {
    id: 5,
    name: 'Verified Customer',
    company: 'Santa Ana, CA',
    rating: 5,
    text: 'What an amazing shop, can fulfill any order! Thank you Corporate awards for all your expedite help and great craftsmanship with the orders.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80'
  },
  {
    id: 6,
    name: 'Verified Customer',
    company: 'Santa Ana, CA',
    rating: 5,
    text: 'Called in for a quote. Answered on the first ring and very honest and informative. Will definitely keep their info and do business in the future.',
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&q=80'
  }
];

const HomePage = () => {

  const services = [
    {
      id: 1,
      title: 'Small Format',
      description: 'Business cards, flyers, brochures & marketing materials',
      icon: Sparkles,
      link: '/products',
    },
    {
      id: 2,
      title: 'Large Format',
      description: 'Banners, signs, vehicle wraps & outdoor displays',
      icon: Zap,
      link: '/LargePrint',
    },
    {
      id: 3,
      title: 'Awards',
      description: 'Trophies, plaques, medals & recognition items',
      icon: Award,
      link: '/Awards',
    },
    {
      id: 4,
      title: 'Promo Swag',
      description: 'Custom branded merchandise & promotional items',
      icon: Users,
      link: '/Swag',
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Premium Quality',
      description: 'Industry-leading materials and state-of-the-art printing technology'
    },
    {
      icon: Truck,
      title: 'Fast Delivery',
      description: 'Quick shipping with expedited options available'
    },
    {
      icon: Clock,
      title: 'Quick Turnaround',
      description: 'Same-day production available on select products'
    },
    {
      icon: Award,
      title: 'Satisfaction Guaranteed',
      description: '100% quality guarantee on every order we produce'
    }
  ];

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Hero Section - Elegant, light, refined */}
      <section className="relative overflow-hidden gradient-hero">
        {/* Subtle decorative elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 pt-20 pb-32 md:pt-28 md:pb-40">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-8 animate-fade-up">
              <Sparkles className="w-4 h-4 text-gold-600" />
              <span className="text-gold-700 text-sm font-medium font-body">Professional Printing & Engraving Services</span>
            </div>
            
            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-semibold mb-6 leading-[1.1] animate-fade-up delay-100">
              <span className="text-ivory-800">Print with</span>
              <br />
              <span className="gradient-text-gold">Precision</span>
            </h1>
            
            {/* Tagline */}
            <p className="text-sm md:text-base text-gold-600 font-medium tracking-wide mb-8 animate-fade-up delay-150 font-body">
              Built on Service • Driven by Pride • Defined by Quality • Perfected by Craftsmanship
            </p>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-ivory-500 max-w-2xl mx-auto mb-12 leading-relaxed font-body animate-fade-up delay-200">
              From business cards to large format displays, we deliver exceptional quality printing and custom awards that elevate your brand.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up delay-300">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 btn-luxury-primary rounded-lg font-body font-medium"
                asChild
              >
                <Link to="/v2/products">
                  Browse Products
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 btn-luxury-outline rounded-lg font-body font-medium"
                asChild
              >
                <Link to="/contact">
                  Contact Sales
                </Link>
              </Button>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-16 animate-fade-up delay-400">
              <div className="flex items-center gap-2 text-ivory-500">
                <div className="flex -space-x-2">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-500 to-gold-400 border-2 border-ivory-50" />
                  ))}
                </div>
                <span className="text-sm font-body">5,000+ Happy Customers</span>
              </div>
              <div className="flex items-center gap-1 text-ivory-500">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                ))}
                <span className="text-sm ml-2 font-body">5.0 Rating</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Subtle gradient backgrounds */}
      <section className="relative py-24 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
              Our Services
            </h2>
            <p className="text-ivory-500 text-lg max-w-2xl mx-auto font-body">
              Comprehensive printing and promotional solutions for every business need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {services.map((service, index) => (
              <Link
                key={service.id}
                to={service.link}
                className="group relative"
                data-testid={`service-card-${service.id}`}
              >
                <div className="relative h-full p-8 rounded-xl bg-ivory-100 border border-ivory-300/50 overflow-hidden card-luxury">
                  {/* Subtle gradient on hover */}
                  <div className="absolute inset-0 gradient-champagne opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Icon */}
                  <div className="relative w-14 h-14 rounded-lg bg-gradient-to-br from-gold-600 to-gold-500 flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300 shadow-luxury">
                    <service.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="relative text-xl font-heading font-semibold text-ivory-800 mb-3 group-hover:text-gold-700 transition-colors">
                    {service.title}
                  </h3>
                  <p className="relative text-ivory-500 text-sm leading-relaxed mb-6 font-body">
                    {service.description}
                  </p>
                  
                  {/* Arrow */}
                  <div className="relative flex items-center text-gold-600 font-medium text-sm font-body">
                    <span>Explore</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Categories - Pearl gradient section */}
      <section className="relative py-24 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
                Popular Categories
              </h2>
              <p className="text-ivory-500 text-lg font-body">
                Explore our most requested print products
              </p>
            </div>
            <Button 
              variant="ghost" 
              className="text-gold-600 hover:text-gold-700 hover:bg-gold-500/10 mt-4 md:mt-0 font-body"
              asChild
            >
              <Link to="/products">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredCategories.map((category, index) => (
              <Link
                key={category.id}
                to={`/products/${category.id}`}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-luxury-lg"
                data-testid={`category-card-${category.id}`}
              >
                {/* Image */}
                <img
                  src={category.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                
                {/* Overlay - elegant gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-ivory-900/80 via-ivory-900/30 to-transparent" />
                
                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <h3 className="text-2xl font-heading font-semibold text-white mb-2 group-hover:text-gold-300 transition-colors">
                    {category.name}
                  </h3>
                  <p className="text-ivory-200 text-sm mb-4 line-clamp-2 font-body">
                    {category.description}
                  </p>
                  <div className="flex items-center text-gold-400 font-medium text-sm opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 font-body">
                    <span>Shop Now</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Stone gradient */}
      <section className="relative py-24 gradient-stone">
        <div className="relative container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
              Why Choose Engraveca
            </h2>
            <p className="text-ivory-500 text-lg max-w-2xl mx-auto font-body">
              We&apos;re committed to delivering exceptional quality and service
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="text-center group"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 rounded-xl bg-ivory-100 border border-gold-500/20 flex items-center justify-center mx-auto mb-6 group-hover:border-gold-500/40 group-hover:scale-105 transition-all duration-300 shadow-luxury">
                  <feature.icon className="w-8 h-8 text-gold-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-ivory-500 text-sm leading-relaxed font-body">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Clients Section */}
      <TrustedClients />

      {/* Testimonials Section */}
      <section className="relative py-24 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-1 mb-4">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-5 h-5 fill-gold-500 text-gold-500" />
              ))}
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-ivory-500 text-lg font-body">
              Trusted by thousands of businesses nationwide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {testimonials.slice(0, 6).map((testimonial) => (
              <div 
                key={testimonial.id}
                className="p-8 rounded-xl bg-ivory-100 border border-ivory-300/50 card-luxury"
                data-testid={`testimonial-${testimonial.id}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-ivory-600 leading-relaxed mb-6 line-clamp-4 font-body">
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-gold-500/20"
                  />
                  <div>
                    <div className="font-semibold text-ivory-800 font-body">{testimonial.name}</div>
                    <div className="text-sm text-ivory-500 font-body">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-ivory-500 text-sm mb-4 font-body">Based on verified reviews from Google and Yelp</p>
            <div className="flex justify-center gap-6">
              <a 
                href="https://www.yelp.com/biz/corporate-awards-and-engraving-santa-ana" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gold-600 hover:text-gold-700 font-medium text-sm transition-colors font-body"
              >
                View Yelp Reviews →
              </a>
              <a 
                href="https://maps.google.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gold-600 hover:text-gold-700 font-medium text-sm transition-colors font-body"
              >
                View Google Reviews →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Feed Section */}
      <SocialFeed />

      {/* CTA Section - Elegant champagne gold */}
      <section className="relative py-24 overflow-hidden">
        {/* Background - refined gold gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-800/30 rounded-full blur-[80px]" />
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-semibold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gold-100 mb-10 max-w-2xl mx-auto font-body">
            Join thousands of businesses that trust Engraveca for their printing and promotional needs
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 bg-white text-gold-700 hover:bg-ivory-100 rounded-lg shadow-luxury-lg transition-all duration-300 hover:scale-105 font-body font-semibold"
              asChild
            >
              <Link to="/v2/products" data-testid="cta-browse-products">
                Browse Products
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6 border-2 border-white/30 bg-transparent text-white hover:bg-white/10 rounded-lg transition-all duration-300 font-body font-medium"
              asChild
            >
              <Link to="/contact" data-testid="cta-contact-sales">
                Contact Sales
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
